import { useEffect, useMemo } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdfDraftPatchSchema, type MdfDraftPatchInput } from "@colab/shared";
import { useDebouncedCallback } from "@/hooks/use-debounced-callback";
import { useMdfDraft } from "./use-mdf-draft";
import { useSaveMdfDraft } from "./use-save-mdf-draft";
import { useUploadMdfAttachment } from "./use-upload-mdf-attachment";
import { useRemoveMdfAttachment } from "./use-remove-mdf-attachment";
import { useSubmitMdf } from "./use-submit-mdf";

// Orchestrator hook — mirrors bucket 6's use-form-runtime convention.
//
// Wires together:
//   • react-hook-form (with zodResolver(mdfDraftPatchSchema)) for client-side
//     field-level validation while editing.
//   • useMdfDraft for the read (allocation balance + persisted attachments).
//   • useSaveMdfDraft + a 500ms debounced callback for auto-save.
//   • useUploadMdfAttachment / useRemoveMdfAttachment for attachments.
//   • useSubmitMdf for the final promote (the caller invokes form.handleSubmit
//     against a stricter submit schema; see use-submit-mdf for the call shape).
//
// The "Saving / Saved / Error" indicator comes from `save.isPending / .isSuccess
// / .isError`. Cache state is NOT used to drive the indicator — the draft
// body lives in react-hook-form, and saves are local-state-only by design.
//
// Phase 7's MDF draft page consumes this hook directly; the page itself
// stays slim. Future buckets that need similar runtime hooks (e.g. MDF
// admin approval flow with notes) should mirror this shape.

export type MdfRuntime = {
  form: UseFormReturn<MdfDraftPatchInput>;
  draftQuery: ReturnType<typeof useMdfDraft>;
  save: ReturnType<typeof useSaveMdfDraft>;
  upload: ReturnType<typeof useUploadMdfAttachment>;
  remove: ReturnType<typeof useRemoveMdfAttachment>;
  submit: ReturnType<typeof useSubmitMdf>;
};

export function useMdfRuntime(draftId: string): MdfRuntime {
  const draftQuery = useMdfDraft(draftId);
  const save = useSaveMdfDraft(draftId);
  const upload = useUploadMdfAttachment(draftId);
  const remove = useRemoveMdfAttachment(draftId);
  const submit = useSubmitMdf(draftId);

  const initialValues = useMemo<MdfDraftPatchInput>(() => {
    const d = draftQuery.data?.draft;
    return {
      amount_eur: d?.amount_eur ?? null,
      campaign_name: d?.campaign_name ?? null,
      campaign_type: d?.campaign_type ?? null,
      description: d?.description ?? null,
    };
  }, [draftQuery.data?.draft]);

  const form = useForm<MdfDraftPatchInput>({
    resolver: zodResolver(mdfDraftPatchSchema),
    defaultValues: initialValues,
  });

  // Reset the form when the underlying draft loads / changes (e.g. navigating
  // between drafts). Without this the defaults from the first render stick.
  useEffect(() => {
    if (draftQuery.data?.draft) {
      form.reset(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQuery.data?.draft?.id]);

  const debouncedSave = useDebouncedCallback((values: MdfDraftPatchInput) => {
    save.mutate(values);
  }, 500);

  // Subscribe to form changes; the watcher fires per field edit. RHF's
  // watch returns the entire form state on each change.
  useEffect(() => {
    const sub = form.watch((values) => {
      debouncedSave(values as MdfDraftPatchInput);
    });
    return () => sub.unsubscribe();
  }, [form, debouncedSave]);

  return { form, draftQuery, save, upload, remove, submit };
}
